/**
 * AdminSidebar - Menu lateral do painel Admin Root
 * Usa branding dinâmico (logo/ícone/nome) da tabela system_settings
 * Organizado por seções agrupadas para melhor navegação
 * Suporte a internacionalização (i18n)
 */

import { Building2, Users, Settings, LayoutDashboard, Shield, Plug, CreditCard, ListTodo, Activity, Zap, Megaphone, Database, Skull, Grid3X3, MessageSquare, Package, ServerCog, Gauge, Wrench, Radio, Scale, Webhook, DollarSign, FileSignature, KeyRound, Brain } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from '@/components/NavLink';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { useSystemSettings } from '@/hooks/useSystemSettings';

// Grupos de menu organizados por seção lógica com chaves de tradução
const menuGroups = [
  {
    labelKey: 'admin.groups.dashboard',
    items: [
      { titleKey: 'admin.items.dashboard', url: '/admin', icon: LayoutDashboard },
    ]
  },
  {
    labelKey: 'admin.groups.plataforma',
    items: [
      { titleKey: 'admin.items.tenants', url: '/admin/tenants', icon: Building2 },
      { titleKey: 'admin.items.gerenciamentoTenant', url: '/admin/tenant-management', icon: Gauge },
      { titleKey: 'admin.items.auditoriaLimites', url: '/admin/tenant-limits', icon: Scale },
      { titleKey: 'admin.items.financeiro', url: '/admin/financial', icon: DollarSign },
      { titleKey: 'admin.items.usuarios', url: '/admin/users', icon: Users },
      { titleKey: 'admin.items.papeis', url: '/admin/roles', icon: Shield },
    ]
  },
  {
    labelKey: 'admin.groups.configuracoes',
    items: [
      { titleKey: 'admin.items.planos', url: '/admin/plans', icon: CreditCard },
      { titleKey: 'admin.items.addons', url: '/admin/addons', icon: Package },
      { titleKey: 'admin.items.tiposCanal', url: '/admin/channel-types', icon: MessageSquare },
      { titleKey: 'admin.items.matrizPlanos', url: '/admin/plan-matrix', icon: Grid3X3 },
      { titleKey: 'admin.items.providers', url: '/admin/providers', icon: Plug },
      { titleKey: 'admin.items.instancias', url: '/admin/provider-instances', icon: ServerCog },
      { titleKey: 'admin.items.gestaoAddons', url: '/admin/addon-management', icon: Wrench },
    ]
  },
  {
    labelKey: 'admin.groups.monitoramento',
    items: [
      { titleKey: 'admin.items.wahaMonitor', url: '/admin/waha-monitor', icon: Radio },
      { titleKey: 'admin.items.filaFluxos', url: '/admin/queue', icon: ListTodo },
      { titleKey: 'admin.items.filaCampanhas', url: '/admin/campaign-queue', icon: Megaphone },
      { titleKey: 'admin.items.filaWebhooks', url: '/admin/webhook-queue', icon: Webhook },
      { titleKey: 'admin.items.rawIngest', url: '/admin/raw-ingest', icon: Database },
      { titleKey: 'admin.items.filaContratos', url: '/admin/contract-queue', icon: FileSignature },
      { titleKey: 'admin.items.filaFinanceiro', url: '/admin/financial-queue', icon: DollarSign },
      { titleKey: 'admin.items.dlq', url: '/admin/dlq', icon: Skull },
      { titleKey: 'admin.items.sessoesSuporte', url: '/admin/sessions', icon: KeyRound },
      { titleKey: 'admin.items.observabilidade', url: '/admin/observability', icon: Activity },
      { titleKey: 'admin.items.dbPerformance', url: '/admin/db-performance', icon: Gauge },
      { titleKey: 'admin.items.chatPerformance', url: '/admin/chat-performance', icon: MessageSquare },
      { titleKey: 'admin.items.stressTest', url: '/admin/stress-test', icon: Zap },
      { titleKey: 'admin.items.stressTestCampanhas', url: '/admin/campaign-stress-test', icon: Megaphone },
      { titleKey: 'admin.items.configuracoes', url: '/admin/settings', icon: Settings },
    ]
  },
  {
    labelKey: 'admin.groups.baseConhecimento',
    items: [
      { titleKey: 'admin.items.pipelineKb', url: '/admin/kb-pipeline', icon: Brain },
      { titleKey: 'admin.items.kbAiObservability', url: '/admin/kb-ai-observability', icon: Activity },
    ]
  },
];

export function AdminSidebar() {
  const { state, isMobile } = useSidebar();
  // No mobile, sempre mostrar expandido quando Sheet está aberto
  const collapsed = isMobile ? false : state === 'collapsed';
  const { branding } = useSystemSettings();
  const { t } = useTranslation('sidebar');

  return (
    <Sidebar collapsible="icon">
      {/* Header com branding dinâmico */}
      <SidebarHeader className="border-b border-sidebar-border/50 px-3 py-3">
        <div className="flex items-center gap-3">
          {branding.icon_url ? (
            <img 
              src={branding.icon_url} 
              alt="" 
              className="h-8 w-8 shrink-0 object-contain rounded-lg"
            />
          ) : (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">
              {branding.app_name[0]?.toUpperCase() || 'U'}
            </div>
          )}
          {!collapsed && (
            <span className="font-semibold text-foreground truncate">
              {branding.app_name}
            </span>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 py-1">
        <div className="space-y-0">
          {menuGroups.map((group, groupIndex) => (
            <SidebarGroup key={group.labelKey} className="py-0">
              {/* Label da seção - visível apenas quando não está colapsado */}
              {!collapsed && (
                <SidebarGroupLabel className="text-[10px] font-semibold text-sidebar-foreground/50 uppercase tracking-wider px-2 mb-0 py-1">
                  {t(group.labelKey)}
                </SidebarGroupLabel>
              )}
              
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.items.map((item) => (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          end={item.url === '/admin'}
className="flex items-center gap-3 px-3 py-1 rounded-lg transition-all duration-200 ease-in-out text-sidebar-foreground/90 hover:bg-sidebar-accent hover:text-sidebar-foreground hover:translate-x-1"
                          activeClassName="bg-sidebar-primary/20 text-sidebar-primary font-medium border-l-4 border-sidebar-primary -ml-[2px] pl-[14px] hover:translate-x-0"
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          {!collapsed && <span className="truncate text-sm">{t(item.titleKey)}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>

              {/* Separador entre grupos (exceto o último) */}
              {groupIndex < menuGroups.length - 1 && !collapsed && (
                <Separator className="mt-1 bg-sidebar-border opacity-50" />
              )}
            </SidebarGroup>
          ))}
        </div>
      </SidebarContent>
    </Sidebar>
  );
}
